import React, { useState, useEffect, useRef } from 'react';
import { homepageAPI } from './services/api';

interface AboutContent {
  id: number;
  title: string;
  paragraph1: string;
  paragraph2: string;
  image_url: string;
}

interface HomepageSection {
  id: number;
  title: string;
  content: string;
  image_url: string;
  button_label: string;
  button_action: string;
  sort_order: number;
  is_active: boolean;
}

interface HeaderData {
  id: number;
  site_name: string;
  logo_url: string;
  hero_title: string;
  hero_subtitle: string;
}

interface FooterData {
  id: number;
  tagline: string;
  instagram_url: string;
  facebook_url: string;
  copyright_text: string;
}

interface PolicyData {
  id: number;
  policy_key: string;
  title: string;
  content: string;
  is_active: boolean;
}

interface SlideData {
  id: number;
  image_url: string;
  sort_order: number;
  is_active: boolean;
}

type MainTab = 'content' | 'header' | 'footer' | 'policies';

const BUTTON_ACTIONS = [
  { value: '', label: 'Buton Yok' },
  { value: 'our-rooms', label: 'Odalarımız sayfası' },
  { value: 'rooms', label: 'Rezervasyon (Oda Ara)' },
  { value: 'sunbed', label: 'Şezlong Kiralama' },
  { value: 'breakfast', label: 'Kahvaltı Rezervasyonu' },
  { value: 'dinner', label: 'Akşam Yemeği' },
];

const emptySection: Omit<HomepageSection, 'id' | 'sort_order' | 'is_active'> = {
  title: '',
  content: '',
  image_url: '',
  button_label: '',
  button_action: '',
};

const HomepageContentTab: React.FC = () => {
  const [mainTab, setMainTab] = useState<MainTab>('content');
  const [about, setAbout] = useState<AboutContent | null>(null);
  const [sections, setSections] = useState<HomepageSection[]>([]);
  const [header, setHeader] = useState<HeaderData | null>(null);
  const [footer, setFooter] = useState<FooterData | null>(null);
  const [policies, setPolicies] = useState<PolicyData[]>([]);
  const [editingPolicy, setEditingPolicy] = useState<PolicyData | null>(null);
  const [slides, setSlides] = useState<SlideData[]>([]);
  const [uploadingSlide, setUploadingSlide] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Editing state
  const [editingSection, setEditingSection] = useState<(Partial<HomepageSection> & { isNew?: boolean }) | null>(null);
  const [uploadingId, setUploadingId] = useState<number | string | null>(null);
  const sectionImageRef = useRef<HTMLInputElement>(null);
  const aboutImageRef = useRef<HTMLInputElement>(null);
  const logoRef = useRef<HTMLInputElement>(null);
  const slideUploadRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadContent();
  }, []);

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const loadContent = async () => {
    try {
      setLoading(true);
      const [contentRes, sectionsRes, policiesRes, slidesRes] = await Promise.all([
        homepageAPI.getContent(),
        homepageAPI.getSections(),
        homepageAPI.getAllPolicies(),
        homepageAPI.getSlides(),
      ]);
      if (contentRes.data.success) {
        const d = contentRes.data.data;
        setAbout(d.about);
        setHeader(d.header);
        setFooter(d.footer);
      }
      if (sectionsRes.data.success) setSections(sectionsRes.data.data);
      if (policiesRes.data.success) setPolicies(policiesRes.data.data);
      if (slidesRes.data.success) setSlides(slidesRes.data.data);
    } catch {
      showMessage('error', 'İçerik yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // ── About Section ──────────────────────────────────────────────────────────

  const handleSaveAbout = async () => {
    if (!about) return;
    try {
      setSaving(true);
      await homepageAPI.updateAbout({
        title: about.title,
        paragraph1: about.paragraph1,
        paragraph2: about.paragraph2,
      });
      showMessage('success', 'Hakkımızda bölümü kaydedildi');
    } catch {
      showMessage('error', 'Kaydedilirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleAboutImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !about) return;
    const formData = new FormData();
    formData.append('image', file);
    try {
      setUploadingId('about');
      const res = await homepageAPI.uploadAboutImage(formData);
      if (res.data.success) {
        setAbout(prev => prev ? { ...prev, image_url: res.data.data.url } : prev);
        showMessage('success', 'Resim yüklendi');
      }
    } catch {
      showMessage('error', 'Resim yüklenirken hata oluştu');
    } finally {
      setUploadingId(null);
      if (aboutImageRef.current) aboutImageRef.current.value = '';
    }
  };

  // ── Sections ───────────────────────────────────────────────────────────────

  const handleEditSection = (section: HomepageSection) => {
    setEditingSection({ ...section });
  };

  const handleNewSection = () => {
    setEditingSection({ ...emptySection, isNew: true });
  };

  const handleCancelEdit = () => {
    setEditingSection(null);
    if (sectionImageRef.current) sectionImageRef.current.value = '';
  };

  const handleSaveSection = async () => {
    if (!editingSection) return;
    if (!editingSection.title?.trim()) {
      showMessage('error', 'Bölüm başlığı boş olamaz');
      return;
    }
    try {
      setSaving(true);
      if (editingSection.isNew) {
        const res = await homepageAPI.createSection({
          title: editingSection.title,
          content: editingSection.content || '',
          image_url: editingSection.image_url || '',
          button_label: editingSection.button_label || '',
          button_action: editingSection.button_action || '',
        });
        if (res.data.success) {
          setSections(prev => [...prev, res.data.data]);
          showMessage('success', 'Yeni bölüm eklendi');
        }
      } else {
        const res = await homepageAPI.updateSection(editingSection.id!, {
          title: editingSection.title,
          content: editingSection.content,
          image_url: editingSection.image_url,
          button_label: editingSection.button_label,
          button_action: editingSection.button_action,
          is_active: editingSection.is_active,
        });
        if (res.data.success) {
          setSections(prev => prev.map(s => s.id === editingSection.id ? res.data.data : s));
          showMessage('success', 'Bölüm güncellendi');
        }
      }
      setEditingSection(null);
    } catch {
      showMessage('error', 'Kaydedilirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSection = async (id: number) => {
    if (!window.confirm('Bu bölümü silmek istediğinizden emin misiniz?')) return;
    try {
      await homepageAPI.deleteSection(id);
      setSections(prev => prev.filter(s => s.id !== id));
      if (editingSection?.id === id) setEditingSection(null);
      showMessage('success', 'Bölüm silindi');
    } catch {
      showMessage('error', 'Silme işlemi başarısız');
    }
  };

  const handleSectionImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editingSection?.id) return;
    const formData = new FormData();
    formData.append('image', file);
    try {
      setUploadingId(editingSection.id);
      const res = await homepageAPI.uploadSectionImage(editingSection.id as number, formData);
      if (res.data.success) {
        const newUrl = res.data.data.url;
        setEditingSection(prev => prev ? { ...prev, image_url: newUrl } : prev);
        setSections(prev => prev.map(s => s.id === editingSection.id ? { ...s, image_url: newUrl } : s));
        showMessage('success', 'Resim yüklendi');
      }
    } catch {
      showMessage('error', 'Resim yüklenirken hata oluştu');
    } finally {
      setUploadingId(null);
      if (sectionImageRef.current) sectionImageRef.current.value = '';
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    const newSections = [...sections];
    [newSections[index - 1], newSections[index]] = [newSections[index], newSections[index - 1]];
    const reordered = newSections.map((s, i) => ({ ...s, sort_order: i }));
    setSections(reordered);
    try {
      await homepageAPI.reorder(reordered.map(s => ({ id: s.id, sort_order: s.sort_order })));
    } catch {
      showMessage('error', 'Sıralama kaydedilemedi');
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === sections.length - 1) return;
    const newSections = [...sections];
    [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];
    const reordered = newSections.map((s, i) => ({ ...s, sort_order: i }));
    setSections(reordered);
    try {
      await homepageAPI.reorder(reordered.map(s => ({ id: s.id, sort_order: s.sort_order })));
    } catch {
      showMessage('error', 'Sıralama kaydedilemedi');
    }
  };

  // ── Header ─────────────────────────────────────────────────────────────────

  const handleSaveHeader = async () => {
    if (!header) return;
    try {
      setSaving(true);
      await homepageAPI.updateHeader({
        site_name: header.site_name,
        logo_url: header.logo_url,
        hero_title: header.hero_title,
        hero_subtitle: header.hero_subtitle,
      });
      showMessage('success', 'Header kaydedildi');
    } catch {
      showMessage('error', 'Kaydedilirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    try {
      setUploadingId('logo');
      const res = await homepageAPI.uploadLogo(formData);
      if (res.data.success) {
        setHeader(prev => prev ? { ...prev, logo_url: res.data.data.url } : prev);
        showMessage('success', 'Logo yüklendi');
      }
    } catch {
      showMessage('error', 'Logo yüklenirken hata oluştu');
    } finally {
      setUploadingId(null);
      if (logoRef.current) logoRef.current.value = '';
    }
  };

  // ── Slides ─────────────────────────────────────────────────────────────────

  const handleAddSlide = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    try {
      setUploadingSlide(true);
      const res = await homepageAPI.addSlide(formData);
      if (res.data.success) {
        setSlides(prev => [...prev, res.data.data]);
        showMessage('success', 'Slide eklendi');
      }
    } catch {
      showMessage('error', 'Slide eklenirken hata oluştu');
    } finally {
      setUploadingSlide(false);
      if (slideUploadRef.current) slideUploadRef.current.value = '';
    }
  };

  const handleDeleteSlide = async (id: number) => {
    if (!window.confirm('Bu slide\'ı silmek istediğinizden emin misiniz?')) return;
    try {
      await homepageAPI.deleteSlide(id);
      setSlides(prev => prev.filter(s => s.id !== id));
      showMessage('success', 'Slide silindi');
    } catch {
      showMessage('error', 'Slide silinirken hata oluştu');
    }
  };

  const handleMoveSlide = async (index: number, direction: 'up' | 'down') => {
    const n = [...slides];
    const swapIdx = direction === 'up' ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= n.length) return;
    [n[index], n[swapIdx]] = [n[swapIdx], n[index]];
    const reordered = n.map((s, i) => ({ ...s, sort_order: i }));
    setSlides(reordered);
    try {
      await homepageAPI.reorderSlides(reordered.map(s => ({ id: s.id, sort_order: s.sort_order })));
    } catch {
      showMessage('error', 'Sıralama kaydedilemedi');
    }
  };

  // ── Footer ─────────────────────────────────────────────────────────────────

  const handleSaveFooter = async () => {
    if (!footer) return;
    try {
      setSaving(true);
      await homepageAPI.updateFooter({
        tagline: footer.tagline,
        instagram_url: footer.instagram_url,
        facebook_url: footer.facebook_url || '',
        copyright_text: footer.copyright_text,
      });
      showMessage('success', 'Footer kaydedildi');
    } catch {
      showMessage('error', 'Kaydedilirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  // ── Policies ───────────────────────────────────────────────────────────────

  const handleSavePolicy = async () => {
    if (!editingPolicy) return;
    try {
      setSaving(true);
      await homepageAPI.updatePolicy(editingPolicy.policy_key, {
        title: editingPolicy.title,
        content: editingPolicy.content,
        is_active: editingPolicy.is_active,
      });
      setPolicies(prev => prev.map(p => p.policy_key === editingPolicy.policy_key ? { ...editingPolicy } : p));
      showMessage('success', 'Politika kaydedildi');
    } catch {
      showMessage('error', 'Kaydedilirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="tab-content" style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
        <div>⏳ Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="tab-content">
      <h2>🏠 Ana Sayfa Yönetimi</h2>

      {/* Mesaj */}
      {message && (
        <div style={{
          padding: '0.8rem 1.2rem',
          marginBottom: '1.5rem',
          borderRadius: '8px',
          backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
          color: message.type === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
        }}>
          {message.type === 'success' ? '✅' : '❌'} {message.text}
        </div>
      )}

      {/* ── Sekme Gezgini ─────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '2px solid #e0e0e0' }}>
        {([
          { key: 'content' as MainTab, label: '📝 İçerik' },
          { key: 'header' as MainTab, label: '🔝 Header' },
          { key: 'footer' as MainTab, label: '🔻 Footer' },
          { key: 'policies' as MainTab, label: '📄 Politikalar' },
        ]).map(t => (
          <button key={t.key} onClick={() => setMainTab(t.key)} style={{
            padding: '0.6rem 1.1rem',
            border: 'none',
            borderBottom: `3px solid ${mainTab === t.key ? '#3498db' : 'transparent'}`,
            backgroundColor: 'transparent',
            cursor: 'pointer',
            fontWeight: mainTab === t.key ? 700 : 400,
            color: mainTab === t.key ? '#3498db' : '#555',
            fontSize: '0.9rem',
            borderRadius: '6px 6px 0 0',
            marginBottom: '-2px',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ═══════════════ CONTENT TAB ══════════════════════════════════════════ */}
      {mainTab === 'content' && (<>

      {/* ── Hakkımızda ───────────────────────────────────────────── */}
      <div style={{
        backgroundColor: '#fff',
        border: '1px solid #e0e0e0',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '2rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
      }}>
        <h3 style={{ marginBottom: '1.2rem', color: '#2c3e50' }}>📝 Hakkımızda Bölümü</h3>

        <div style={{ display: 'grid', gap: '1rem' }}>
          <div>
            <label style={labelStyle}>Başlık</label>
            <input
              style={inputStyle}
              value={about?.title || ''}
              onChange={e => setAbout(prev => prev ? { ...prev, title: e.target.value } : prev)}
              placeholder="Hakkımızda"
            />
          </div>
          <div>
            <label style={labelStyle}>1. Paragraf</label>
            <textarea
              style={{ ...inputStyle, height: '90px', resize: 'vertical' }}
              value={about?.paragraph1 || ''}
              onChange={e => setAbout(prev => prev ? { ...prev, paragraph1: e.target.value } : prev)}
              placeholder="Birinci paragraf..."
            />
          </div>
          <div>
            <label style={labelStyle}>2. Paragraf</label>
            <textarea
              style={{ ...inputStyle, height: '90px', resize: 'vertical' }}
              value={about?.paragraph2 || ''}
              onChange={e => setAbout(prev => prev ? { ...prev, paragraph2: e.target.value } : prev)}
              placeholder="İkinci paragraf..."
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            {about?.image_url && (
              <img src={about.image_url} alt="Hakkımızda" style={{ height: 60, width: 90, objectFit: 'cover', borderRadius: 6 }} />
            )}
            <div>
              <input type="file" accept="image/*" ref={aboutImageRef} style={{ display: 'none' }} onChange={handleAboutImageUpload} />
              <button style={btnSecondaryStyle} onClick={() => aboutImageRef.current?.click()} disabled={uploadingId === 'about'}>
                {uploadingId === 'about' ? '⏳ Yükleniyor...' : '🖼️ Resim Yükle (Opsiyonel)'}
              </button>
            </div>
          </div>

          <div>
            <button style={btnPrimaryStyle} onClick={handleSaveAbout} disabled={saving}>
              {saving ? '⏳ Kaydediliyor...' : '💾 Kaydet'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Bölümler Listesi ─────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: editingSection ? '1fr 1.3fr' : '1fr', gap: '1.5rem' }}>
        {/* Sol: Liste */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ color: '#2c3e50', margin: 0 }}>📋 Özellik Bölümleri</h3>
            <button style={btnPrimaryStyle} onClick={handleNewSection}>+ Yeni Bölüm</button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
            {sections.map((section, index) => (
              <div
                key={section.id}
                style={{
                  backgroundColor: editingSection?.id === section.id ? '#f0f7ff' : '#fff',
                  border: `1px solid ${editingSection?.id === section.id ? '#3498db' : '#e0e0e0'}`,
                  borderRadius: '10px',
                  padding: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.8rem',
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                  transition: 'all 0.2s',
                }}
                onClick={() => handleEditSection(section)}
              >
                {section.image_url ? (
                  <img src={section.image_url} alt={section.title} style={{ width: 52, height: 40, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 52, height: 40, backgroundColor: '#f0f0f0', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '1.2rem' }}>🖼️</div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: '#2c3e50', fontSize: '0.95rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{section.title}</div>
                  <div style={{ color: '#666', fontSize: '0.78rem', marginTop: 2 }}>
                    {section.is_active ? '✅ Aktif' : '⭕ Pasif'} · Sıra {index + 1}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }} onClick={e => e.stopPropagation()}>
                  <button style={arrowBtnStyle} onClick={() => handleMoveUp(index)} disabled={index === 0} title="Yukarı">▲</button>
                  <button style={arrowBtnStyle} onClick={() => handleMoveDown(index)} disabled={index === sections.length - 1} title="Aşağı">▼</button>
                </div>
              </div>
            ))}
            {sections.length === 0 && (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#999', backgroundColor: '#f8f9fa', borderRadius: 10 }}>
                Henüz bölüm yok. "Yeni Bölüm" butonuyla ekleyin.
              </div>
            )}
          </div>
        </div>

        {/* Sağ: Düzenleme Paneli */}
        {editingSection && (
          <div style={{
            backgroundColor: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
            position: 'sticky',
            top: '1rem',
            alignSelf: 'start',
          }}>
            <h3 style={{ marginBottom: '1.2rem', color: '#2c3e50' }}>
              {editingSection.isNew ? '➕ Yeni Bölüm' : `✏️ Düzenle: ${editingSection.title}`}
            </h3>

            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={labelStyle}>Başlık *</label>
                <input
                  style={inputStyle}
                  value={editingSection.title || ''}
                  onChange={e => setEditingSection(prev => prev ? { ...prev, title: e.target.value } : prev)}
                  placeholder="Örn: Villamız"
                />
              </div>

              <div>
                <label style={labelStyle}>İçerik / Açıklama</label>
                <textarea
                  style={{ ...inputStyle, height: '110px', resize: 'vertical' }}
                  value={editingSection.content || ''}
                  onChange={e => setEditingSection(prev => prev ? { ...prev, content: e.target.value } : prev)}
                  placeholder="Bölüm açıklaması..."
                />
              </div>

              <div>
                <label style={labelStyle}>Resim</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap' }}>
                  {editingSection.image_url && (
                    <img src={editingSection.image_url} alt="Önizleme" style={{ height: 56, width: 80, objectFit: 'cover', borderRadius: 6 }} />
                  )}
                  <div>
                    <input type="file" accept="image/*" ref={sectionImageRef} style={{ display: 'none' }} onChange={handleSectionImageUpload} />
                    <button
                      style={btnSecondaryStyle}
                      onClick={() => sectionImageRef.current?.click()}
                      disabled={uploadingId === editingSection.id || editingSection.isNew}
                      title={editingSection.isNew ? 'Önce kaydedin, sonra resim yükleyin' : ''}
                    >
                      {uploadingId === editingSection.id ? '⏳ Yükleniyor...' : '🖼️ Resim Yükle'}
                    </button>
                    {editingSection.isNew && (
                      <div style={{ fontSize: '0.75rem', color: '#888', marginTop: 4 }}>* Önce kaydedin, sonra resim ekleyin</div>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem' }}>
                <div>
                  <label style={labelStyle}>Buton Yazısı</label>
                  <input
                    style={inputStyle}
                    value={editingSection.button_label || ''}
                    onChange={e => setEditingSection(prev => prev ? { ...prev, button_label: e.target.value } : prev)}
                    placeholder="Örn: Daha Fazla"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Buton Yönlendirme</label>
                  <select
                    style={inputStyle}
                    value={editingSection.button_action || ''}
                    onChange={e => setEditingSection(prev => prev ? { ...prev, button_action: e.target.value } : prev)}
                  >
                    {BUTTON_ACTIONS.map(a => (
                      <option key={a.value} value={a.value}>{a.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {!editingSection.isNew && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={editingSection.is_active !== false}
                    onChange={e => setEditingSection(prev => prev ? { ...prev, is_active: e.target.checked } : prev)}
                  />
                  <label htmlFor="is_active" style={{ fontWeight: 500, cursor: 'pointer' }}>Aktif (Ana sayfada göster)</label>
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                <button style={btnPrimaryStyle} onClick={handleSaveSection} disabled={saving}>
                  {saving ? '⏳ Kaydediliyor...' : '💾 Kaydet'}
                </button>
                <button style={btnSecondaryStyle} onClick={handleCancelEdit}>İptal</button>
                {!editingSection.isNew && (
                  <button
                    style={{ ...btnSecondaryStyle, color: '#e74c3c', borderColor: '#e74c3c', marginLeft: 'auto' }}
                    onClick={() => handleDeleteSection(editingSection.id!)}
                  >
                    🗑️ Sil
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      </>) /* end content tab */}

      {/* ═══════════════ HEADER TAB ══════════════════════════════════════════ */}
      {mainTab === 'header' && (<>
        <div style={cardStyle}>
          <h3 style={{ marginBottom: '0.3rem', color: '#2c3e50' }}>🔝 Header / Navbar Ayarları</h3>
          <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: '1.2rem' }}>Site adı ve logo ana sayfada ve navbar'da görünür.</p>
          <div style={{ display: 'grid', gap: '1.2rem' }}>
            <div>
              <label style={labelStyle}>Site Adı (Navbar'da görünür)</label>
              <input style={inputStyle} value={header?.site_name || ''} onChange={e => setHeader(prev => prev ? { ...prev, site_name: e.target.value } : prev)} placeholder="Villa Polikne" />
            </div>
            <div>
              <label style={labelStyle}>Logo</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                {header?.logo_url && (
                  <img src={header.logo_url} alt="Logo" style={{ height: 50, maxWidth: 120, objectFit: 'contain', borderRadius: 6, border: '1px solid #eee', padding: 4 }} />
                )}
                <div>
                  <input type="file" accept="image/*" ref={logoRef} style={{ display: 'none' }} onChange={handleLogoUpload} />
                  <button style={btnSecondaryStyle} onClick={() => logoRef.current?.click()} disabled={uploadingId === 'logo'}>
                    {uploadingId === 'logo' ? '⏳ Yükleniyor...' : '🖼️ Logo Yükle'}
                  </button>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ ...labelStyle, marginBottom: '0.2rem' }}>veya URL girin</label>
                  <input style={{ ...inputStyle }} value={header?.logo_url || ''} onChange={e => setHeader(prev => prev ? { ...prev, logo_url: e.target.value } : prev)} placeholder="/logo.png" />
                </div>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Hero Başlık (slider üzerinde büyük yazı — opsiyonel)</label>
              <input style={inputStyle} value={header?.hero_title || ''} onChange={e => setHeader(prev => prev ? { ...prev, hero_title: e.target.value } : prev)} placeholder="Boş bırakılırsa gösterilmez" />
            </div>
            <div>
              <label style={labelStyle}>Hero Altbaşlık</label>
              <input style={inputStyle} value={header?.hero_subtitle || ''} onChange={e => setHeader(prev => prev ? { ...prev, hero_subtitle: e.target.value } : prev)} placeholder="Boş bırakılırsa gösterilmez" />
            </div>
            <div>
              <button style={btnPrimaryStyle} onClick={handleSaveHeader} disabled={saving}>
                {saving ? '⏳ Kaydediliyor...' : '💾 Kaydet'}
              </button>
            </div>
          </div>
        </div>

        {/* ── Slider Resimleri ── */}
        <div style={cardStyle}>
          <h3 style={{ marginBottom: '0.3rem', color: '#2c3e50' }}>🎞️ Slider Resimleri</h3>
          <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: '1.2rem' }}>
            Ana sayfada dönen arka plan resimleri. Her 4 saniyede bir sırayla gösterilir. Önerilen boyut: <strong>1920×1080px</strong>
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.2rem' }}>
            {slides.map((slide, index) => (
              <div key={slide.id} style={{ position: 'relative', width: 180, borderRadius: 8, overflow: 'hidden', border: '1px solid #ddd', boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }}>
                <img src={slide.image_url} alt={`Slide ${index + 1}`} style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }} />
                <div style={{ padding: '0.4rem 0.5rem', backgroundColor: '#f8f9fa', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.78rem', color: '#666' }}>#{index + 1}</span>
                  <div style={{ display: 'flex', gap: 3 }}>
                    <button style={arrowBtnStyle} onClick={() => handleMoveSlide(index, 'up')} disabled={index === 0} title="Öne al">◀</button>
                    <button style={arrowBtnStyle} onClick={() => handleMoveSlide(index, 'down')} disabled={index === slides.length - 1} title="Sonraya al">▶</button>
                    <button style={{ ...arrowBtnStyle, color: '#e74c3c', borderColor: '#e74c3c' }} onClick={() => handleDeleteSlide(slide.id)} title="Sil">✕</button>
                  </div>
                </div>
              </div>
            ))}

            {/* Yeni slide ekle */}
            <div
              style={{ width: 180, height: 130, border: '2px dashed #bbb', borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#888', gap: 6 }}
              onClick={() => !uploadingSlide && slideUploadRef.current?.click()}
            >
              <span style={{ fontSize: '1.8rem' }}>{uploadingSlide ? '⏳' : '+'}</span>
              <span style={{ fontSize: '0.78rem' }}>{uploadingSlide ? 'Yükleniyor...' : 'Resim Ekle'}</span>
            </div>
          </div>

          <input type="file" accept="image/*" ref={slideUploadRef} style={{ display: 'none' }} onChange={handleAddSlide} />
        </div>
      </>)}

      {/* ═══════════════ FOOTER TAB ══════════════════════════════════════════ */}
      {mainTab === 'footer' && (
        <div style={cardStyle}>
          <h3 style={{ marginBottom: '0.3rem', color: '#2c3e50' }}>🔻 Footer Ayarları</h3>
          <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: '1.2rem' }}>
            Adres / telefon / e-posta <strong>Genel Ayarlar</strong> sekmesinden düzenlenir ve footerda otomatik gösterilir.
          </p>
          <div style={{ display: 'grid', gap: '1.2rem' }}>
            <div>
              <label style={labelStyle}>Slogan (site adının altındaki kısa yazı)</label>
              <input style={inputStyle} value={footer?.tagline || ''} onChange={e => setFooter(prev => prev ? { ...prev, tagline: e.target.value } : prev)} placeholder="Lüks ve konforun adresi" />
            </div>
            <div>
              <label style={labelStyle}>Instagram URL</label>
              <input style={inputStyle} value={footer?.instagram_url || ''} onChange={e => setFooter(prev => prev ? { ...prev, instagram_url: e.target.value } : prev)} placeholder="https://www.instagram.com/villapolikne" />
            </div>
            <div>
              <label style={labelStyle}>Facebook URL (opsiyonel)</label>
              <input style={inputStyle} value={footer?.facebook_url || ''} onChange={e => setFooter(prev => prev ? { ...prev, facebook_url: e.target.value } : prev)} placeholder="https://www.facebook.com/..." />
            </div>
            <div>
              <label style={labelStyle}>Telif Hakkı Metni</label>
              <input style={inputStyle} value={footer?.copyright_text || ''} onChange={e => setFooter(prev => prev ? { ...prev, copyright_text: e.target.value } : prev)} placeholder="© Villa Polikne. Tüm hakları saklıdır." />
            </div>
            <div>
              <button style={btnPrimaryStyle} onClick={handleSaveFooter} disabled={saving}>
                {saving ? '⏳ Kaydediliyor...' : '💾 Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════ POLICIES TAB ════════════════════════════════════════ */}
      {mainTab === 'policies' && (
        <div style={{ display: 'grid', gridTemplateColumns: editingPolicy ? '280px 1fr' : '1fr', gap: '1.5rem' }}>
          {/* Sol: Politika listesi */}
          <div>
            <h3 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>📄 Footer Politika Linkleri</h3>
            <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: '1rem' }}>Footerda görünen politika bağlantıları. Tıklanınca modal pencere açılır.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
              {policies.map(policy => (
                <div
                  key={policy.policy_key}
                  style={{
                    backgroundColor: editingPolicy?.policy_key === policy.policy_key ? '#f0f7ff' : '#fff',
                    border: `1px solid ${editingPolicy?.policy_key === policy.policy_key ? '#3498db' : '#e0e0e0'}`,
                    borderRadius: '10px',
                    padding: '0.9rem 1.1rem',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                  onClick={() => setEditingPolicy({ ...policy })}
                >
                  <div>
                    <div style={{ fontWeight: 600, color: '#2c3e50', fontSize: '0.93rem' }}>{policy.title}</div>
                    <div style={{ fontSize: '0.75rem', color: '#888', marginTop: 2 }}>
                      {policy.is_active ? '✅ Aktif' : '⭕ Pasif'} · {policy.content ? `${policy.content.replace(/<[^>]+>/g, '').substring(0, 40)}...` : 'İçerik girilmemiş'}
                    </div>
                  </div>
                  <span style={{ color: '#3498db', fontSize: '0.8rem' }}>✏️</span>
                </div>
              ))}
            </div>
          </div>

          {/* Sağ: Düzenleme */}
          {editingPolicy && (
            <div style={{ ...cardStyle, position: 'sticky', top: '1rem', alignSelf: 'start' }}>
              <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>✏️ {editingPolicy.title}</h3>
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div>
                  <label style={labelStyle}>Başlık (linkte ve modalda gösterilir)</label>
                  <input style={inputStyle} value={editingPolicy.title} onChange={e => setEditingPolicy(prev => prev ? { ...prev, title: e.target.value } : prev)} />
                </div>
                <div>
                  <label style={labelStyle}>İçerik</label>
                  <p style={{ fontSize: '0.78rem', color: '#888', margin: '0 0 4px' }}>HTML kullanabilirsiniz: &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;</p>
                  <textarea
                    style={{ ...inputStyle, height: '320px', resize: 'vertical', fontFamily: 'monospace', fontSize: '0.85rem' }}
                    value={editingPolicy.content}
                    onChange={e => setEditingPolicy(prev => prev ? { ...prev, content: e.target.value } : prev)}
                    placeholder="Politika metnini buraya girin..."
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input type="checkbox" id="pol_active" checked={editingPolicy.is_active} onChange={e => setEditingPolicy(prev => prev ? { ...prev, is_active: e.target.checked } : prev)} />
                  <label htmlFor="pol_active" style={{ fontWeight: 500, cursor: 'pointer' }}>Footerda göster</label>
                </div>
                <div style={{ display: 'flex', gap: '0.8rem' }}>
                  <button style={btnPrimaryStyle} onClick={handleSavePolicy} disabled={saving}>{saving ? '⏳ Kaydediliyor...' : '💾 Kaydet'}</button>
                  <button style={btnSecondaryStyle} onClick={() => setEditingPolicy(null)}>İptal</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
};

// Shared styles
const cardStyle: React.CSSProperties = {
  backgroundColor: '#fff',
  border: '1px solid #e0e0e0',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '0.4rem',
  fontWeight: 600,
  fontSize: '0.85rem',
  color: '#555',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.6rem 0.8rem',
  borderRadius: 6,
  border: '1px solid #ddd',
  fontSize: '0.92rem',
  boxSizing: 'border-box',
  outline: 'none',
  fontFamily: 'inherit',
};

const btnPrimaryStyle: React.CSSProperties = {
  padding: '0.55rem 1.2rem',
  backgroundColor: '#3498db',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: '0.9rem',
};

const btnSecondaryStyle: React.CSSProperties = {
  padding: '0.55rem 1.2rem',
  backgroundColor: '#fff',
  color: '#555',
  border: '1px solid #ccc',
  borderRadius: 6,
  cursor: 'pointer',
  fontWeight: 500,
  fontSize: '0.9rem',
};

const arrowBtnStyle: React.CSSProperties = {
  width: 26,
  height: 26,
  padding: 0,
  backgroundColor: '#f0f0f0',
  border: '1px solid #ddd',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: '0.7rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
};

export default HomepageContentTab;
